import { Loader2, Mic, MicOff } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  DICTATION_MAX_SECONDS,
  useDictation,
} from "@/hooks/transcriber/useDictation";
import { cn } from "@/lib/utils";
import { dictationMessage, formatDictationSeconds } from ".";
import { DictationMicMenu } from "./DictationMicMenu";

type Dictation = ReturnType<typeof useDictation>;

// One 36px round control with an invisible 44px tap area (the Ask bar's).
export const ASK_BAR_ROUND =
  "relative inline-flex size-9 shrink-0 items-center justify-center rounded-full after:absolute after:-inset-1 after:content-['']";

/**
 * The v3.2 Ask bar mic (iOS AskBar): the microphone chevron and the round
 * mic, red with a stop square while listening. Shared by the Ask bar and
 * the fields that take a dictated letter or note.
 */
export const AskBarMic = ({
  dictation,
  onToggle,
  disabled = false,
  label,
  testId,
}: {
  dictation: Dictation;
  onToggle: () => void;
  disabled?: boolean;
  // Defaults to "Dictate" / "Stop dictating".
  label?: { start: string; stop: string };
  testId: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const { status, error } = dictation;
  const dictating = status !== "idle";
  const listening = status === "listening";
  const name = dictating
    ? (label?.stop ?? t("dictation.stop"))
    : (label?.start ?? t("dictation.start"));
  return (
    <>
      <DictationMicMenu disabled={dictating || disabled} variant="v3" />
      <button
        aria-label={name}
        aria-pressed={dictating}
        className={ASK_BAR_ROUND}
        data-testid={testId}
        disabled={status === "finishing" || disabled}
        title={name}
        type="button"
        onMouseDown={(event) => {
          // Keep the cursor in the field.
          event.preventDefault();
        }}
        onClick={onToggle}
      >
        <span
          className={cn(
            "inline-flex size-9 items-center justify-center rounded-full",
            listening
              ? "bg-v3-rec text-white ring-4 ring-[color-mix(in_srgb,var(--v3-rec)_30%,transparent)]"
              : "bg-v3-panel text-v3-text shadow-[inset_0_0_0_1px_var(--v3-border-strong),0_1px_2px_rgba(0,0,0,0.06)]",
          )}
        >
          {listening ? (
            <span className="size-3 rounded-[3px] bg-white" />
          ) : dictating ? (
            <Loader2 aria-hidden className="size-4 animate-spin" />
          ) : error ? (
            <MicOff aria-hidden className="size-[18px] text-v3-rec-text" />
          ) : (
            <Mic aria-hidden className="size-[18px]" />
          )}
        </span>
      </button>
    </>
  );
};

/** The Ask bar's status pill: "Listening…", the timer, or the error. */
export const AskBarDictationStatus = ({
  dictation,
  className,
}: {
  dictation: Dictation;
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { status, error, elapsedSeconds } = dictation;
  const dictating = status !== "idle";
  const listening = status === "listening";
  const message =
    dictating || error
      ? dictationMessage(t, { status, error, elapsedSeconds })
      : null;
  if (!message) return null;
  return (
    <p
      aria-live="polite"
      className={cn(
        "flex items-center gap-2 self-start rounded-full bg-v3-panel px-3 py-1.5 text-[12.5px] text-v3-text-2 shadow-v3-float",
        error && !dictating && "text-v3-rec-text",
        className,
      )}
    >
      {listening && (
        <span className="size-1.5 animate-pulse rounded-full bg-v3-rec" />
      )}
      <span>{message}</span>
      {listening && (
        <span className="tabular-nums text-v3-text-3">
          {formatDictationSeconds(elapsedSeconds)} /{" "}
          {formatDictationSeconds(DICTATION_MAX_SECONDS)}
        </span>
      )}
    </p>
  );
};

/**
 * Dictation into a controlled text value, as the Ask bar does: the words go
 * after what was in the field when the mic was pressed, never over it.
 */
export const useDictationInto = (
  value: string,
  onChange: (value: string) => void,
): { dictation: Dictation; toggle: () => void } => {
  const dictation = useDictation();
  const typedBefore = useRef("");
  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the dictated text only.
  useEffect(() => {
    if (dictation.text)
      onChange(
        [typedBefore.current.trimEnd(), dictation.text.trim()]
          .filter(Boolean)
          .join(" "),
      );
  }, [dictation.text]);
  const toggle = (): void => {
    if (dictation.status !== "idle") {
      dictation.stop();
      return;
    }
    typedBefore.current = value;
    void dictation.start();
  };
  return { dictation, toggle };
};
